import React, { useState, type ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type TextInputProps,
} from 'react-native';
import { CalendarDatePicker } from './CalendarDatePicker';
import { TimePicker } from './TimePicker';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { formatPermissionTime } from '../utils/dateTime';
import { isWebPlatform, webPointer } from '../utils/webLayout';

type LabelProps = {
  label: string;
  required?: boolean;
  optional?: boolean;
  locked?: boolean;
  missing?: boolean;
};

export function RequestFormLabel({
  label,
  required,
  optional,
  locked,
  missing,
}: LabelProps) {
  const palette = useAppColors();
  return (
    <Text style={[styles.label, { color: palette.textSecondary }]}>
      {label}
      {required ? (
        <Text style={{ color: palette.error }}> *</Text>
      ) : optional ? (
        <Text style={{ color: palette.textMuted }}> (Optional)</Text>
      ) : null}
      {locked ? (
        <Text style={{ color: palette.textMuted }}> · not editable</Text>
      ) : missing ? (
        <Text style={{ color: palette.textMuted }}> · missing</Text>
      ) : null}
    </Text>
  );
}

type FieldShellProps = {
  children: ReactNode;
  onPress?: () => void;
  accessibilityLabel: string;
  accessibilityHint?: string;
  disabled?: boolean;
};

export function RequestFormFieldShell({
  children,
  onPress,
  accessibilityLabel,
  accessibilityHint,
  disabled,
}: FieldShellProps) {
  const palette = useAppColors();
  const content = (
    <View
      style={[
        styles.field,
        disabled && styles.fieldLocked,
        {
          borderColor: palette.border,
          backgroundColor: disabled ? palette.background : palette.surface,
        },
      ]}
    >
      {children}
    </View>
  );

  if (!onPress) return content;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled: Boolean(disabled) }}
      disabled={disabled}
      onPress={onPress}
      style={webPointer}
    >
      {content}
    </Pressable>
  );
}

type TextFieldProps = {
  label: string;
  required?: boolean;
  optional?: boolean;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  editable?: boolean;
  multiline?: boolean;
  trailing?: ReactNode;
  onPress?: () => void;
} & Pick<TextInputProps, 'keyboardType' | 'autoCapitalize' | 'onFocus' | 'onBlur'>;

export function RequestFormTextField({
  label,
  required,
  optional,
  value,
  onChangeText,
  placeholder,
  editable = true,
  multiline = false,
  trailing,
  onPress,
  keyboardType,
  autoCapitalize,
  onFocus,
  onBlur,
}: TextFieldProps) {
  const palette = useAppColors();
  const inputEditable = editable && !onPress;

  return (
    <View style={styles.container}>
      <RequestFormLabel label={label} required={required} optional={optional} />
      <RequestFormFieldShell
        accessibilityLabel={label}
        onPress={onPress}
        disabled={!editable}
      >
        <TextInput
          accessibilityLabel={label}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={palette.textMuted}
          editable={inputEditable}
          multiline={multiline}
          keyboardType={keyboardType}
          autoCapitalize={autoCapitalize}
          onFocus={onFocus}
          onBlur={onBlur}
          style={[
            styles.input,
            multiline && styles.multiline,
            { color: palette.textPrimary },
            isWebPlatform() ? ({ outlineWidth: 0 } as const) : null,
          ]}
        />
        {trailing}
      </RequestFormFieldShell>
    </View>
  );
}

type AttachmentProps = {
  fileName?: string | null;
  onPress?: () => void;
  disabled?: boolean;
};

export function RequestFormAttachmentField({
  fileName,
  onPress,
  disabled,
}: AttachmentProps) {
  const palette = useAppColors();

  return (
    <View style={styles.container}>
      <RequestFormLabel label="Attachment" optional />
      <RequestFormFieldShell
        accessibilityLabel="Choose File"
        onPress={onPress}
        disabled={disabled}
      >
        <Text
          style={[
            styles.input,
            { color: fileName ? palette.textPrimary : palette.textMuted },
          ]}
        >
          {fileName || 'Choose File'}
        </Text>
        <Text style={[styles.trailingGlyph, { color: palette.textMuted }]}>
          ⧉
        </Text>
      </RequestFormFieldShell>
      <Text style={[styles.helper, { color: palette.textMuted }]}>
        PDF, JPG, PNG (Max 5MB)
      </Text>
    </View>
  );
}

export function RequestFormCalendarIcon({ color }: { color: string }) {
  return <Text style={[styles.trailingGlyph, { color }]}>▦</Text>;
}

type DateFieldProps = {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editable?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
};

/**
 * Apply-form calendar date field. Stores YYYY-MM-DD; opens a calendar picker
 * with no manual text entry.
 */
export function RequestFormDateField({
  label,
  required,
  value,
  onChange,
  placeholder = 'Select date',
  editable = true,
  minimumDate,
  maximumDate,
}: DateFieldProps) {
  const palette = useAppColors();
  const [open, setOpen] = useState(false);
  const displayValue = value.trim();

  return (
    <View style={styles.container}>
      <RequestFormLabel label={label} required={required} />
      <RequestFormFieldShell
        accessibilityLabel={label}
        onPress={() => {
          if (editable) setOpen(true);
        }}
        disabled={!editable}
      >
        <Text
          style={[
            styles.input,
            {
              color: displayValue ? palette.textPrimary : palette.textMuted,
            },
          ]}
        >
          {displayValue || placeholder}
        </Text>
        <RequestFormCalendarIcon color={palette.textMuted} />
      </RequestFormFieldShell>
      <CalendarDatePicker
        open={open}
        value={value}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        onClose={() => setOpen(false)}
        onConfirm={next => {
          onChange(next);
          setOpen(false);
        }}
      />
    </View>
  );
}

export function RequestFormClockIcon({ color }: { color: string }) {
  return <Text style={[styles.trailingGlyph, { color }]}>◷</Text>;
}

type TimeFieldProps = {
  label: string;
  required?: boolean;
  optional?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editable?: boolean;
};

/**
 * Apply-form clock time field. Stores HH:mm; opens a time picker with no
 * manual text entry.
 */
export function RequestFormTimeField({
  label,
  required,
  optional,
  value,
  onChange,
  placeholder = 'Select time',
  editable = true,
}: TimeFieldProps) {
  const palette = useAppColors();
  const [open, setOpen] = useState(false);
  const displayValue = value.trim() ? formatPermissionTime(value.trim()) : '';
  const locked = !editable;

  return (
    <View style={styles.container}>
      <RequestFormLabel
        label={label}
        required={required}
        optional={optional}
        locked={locked}
        missing={!locked && !optional && !displayValue}
      />
      <RequestFormFieldShell
        accessibilityLabel={locked ? `${label}, not editable` : label}
        accessibilityHint={
          locked
            ? 'Existing time cannot be changed'
            : optional
              ? 'Optional. Opens the time picker'
              : 'Opens time picker to enter the missing time'
        }
        onPress={() => {
          if (editable) setOpen(true);
        }}
        disabled={locked}
      >
        <Text
          style={[
            styles.input,
            {
              color: displayValue
                ? locked
                  ? palette.textSecondary
                  : palette.textPrimary
                : palette.textMuted,
            },
          ]}
        >
          {displayValue || placeholder}
        </Text>
        {locked ? (
          <Text style={[styles.lockedBadge, { color: palette.textMuted }]}>
            Locked
          </Text>
        ) : (
          <RequestFormClockIcon color={palette.textMuted} />
        )}
      </RequestFormFieldShell>
      {locked ? (
        <Text style={[styles.helper, { color: palette.textMuted }]}>
          {displayValue
            ? 'Existing time — cannot be changed'
            : 'This field cannot be changed'}
        </Text>
      ) : displayValue && optional ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Clear ${label}`}
          onPress={() => onChange('')}
          style={webPointer}
        >
          <Text style={[styles.helper, { color: palette.primary }]}>Clear</Text>
        </Pressable>
      ) : displayValue ? null : optional ? null : (
        <Text style={[styles.helper, { color: palette.textMuted }]}>
          This time is missing — you can enter it
        </Text>
      )}
      {editable ? (
        <TimePicker
          open={open}
          value={value}
          onClose={() => setOpen(false)}
          onConfirm={next => {
            onChange(next);
            setOpen(false);
          }}
        />
      ) : null}
    </View>
  );
}

export function RequestFormChevronIcon({ color }: { color: string }) {
  return <Text style={[styles.trailingGlyph, { color }]}>▾</Text>;
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  label: {
    ...typography.caption,
    fontWeight: '600',
  },
  field: {
    minHeight: 50,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  fieldLocked: {
    opacity: 0.92,
  },
  lockedBadge: {
    ...typography.caption,
    fontWeight: '700',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  input: {
    ...typography.input,
    flex: 1,
    paddingVertical: spacing.md,
  },
  multiline: {
    minHeight: 96,
    textAlignVertical: 'top',
  },
  trailingGlyph: {
    fontSize: 16,
    fontWeight: '600',
  },
  helper: {
    ...typography.caption,
  },
});
